/**
 * Appearance Settings Page - Couleurs personnalisées
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Palette, User, Building2, Check } from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useTemplateColors } from '@/hooks/useTemplateColors';
import { useUIMode } from '@/hooks/useUIMode';
import { UI_MODE_LABELS } from '@/config/uiModeConfig';
import { notifications } from '@/lib/notifications';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
  focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
  hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
  transition-all duration-300 outline-none text-sm`;

export const AppearanceSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { company, loading, error } = useCompanySettings();
  const { mode, changeMode, isSimpleMode } = useUIMode();

  const {
    preferences: colorPreferences,
    loading: colorsLoading,
    updateColors,
  } = useTemplateColors(company?.id);

  const [tempColors, setTempColors] = useState({
    colorPrimary: colorPreferences?.colorPrimary || '#0d9488',
    colorSecondary: colorPreferences?.colorSecondary || '#14b8a6',
    useCompanyColors: colorPreferences?.useCompanyColors || false,
  });

  useEffect(() => {
    if (colorPreferences) {
      setTempColors({
        colorPrimary: colorPreferences.colorPrimary || '#0d9488',
        colorSecondary: colorPreferences.colorSecondary || '#14b8a6',
        useCompanyColors: colorPreferences.useCompanyColors || false,
      });
    }
  }, [colorPreferences]);

  const handleColorChange = (type: 'primary' | 'secondary', value: string) => {
    setTempColors(prev => ({
      ...prev,
      [type === 'primary' ? 'colorPrimary' : 'colorSecondary']: value,
    }));
  };

  const handleSaveColors = async () => {
    try {
      await updateColors({
        colorPrimary: tempColors.colorPrimary,
        colorSecondary: tempColors.colorSecondary,
        useCompanyColors: tempColors.useCompanyColors,
      });
      notifications.saved('Couleurs');
    } catch (err: any) {
      notifications.saveError('couleurs', err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
            <Palette className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error || t('settings.company.noCompany')}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-pink-100 rounded-xl">
            <Palette className="w-5 h-5 text-pink-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{t('settings.appearance.title')}</h2>
        </div>
        <Button
          onClick={handleSaveColors}
          disabled={colorsLoading}
          variant="secondary"
          icon={<Save className="w-4 h-4" />}
        >
          {colorsLoading ? t('common.actions.saving') : t('common.actions.save')}
        </Button>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Mode d'interface Simple/Expert */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="p-2 bg-indigo-100 rounded-xl">
              <User className="w-5 h-5 text-indigo-600" />
            </div>
            {t('settings.appearance.uiMode')}
          </h3>

          <div className="bg-indigo-50/50 border border-indigo-200/50 rounded-2xl p-3 mb-4">
            <p className="text-sm text-indigo-800">
              {t('settings.appearance.uiModeDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Mode Simple */}
            <button
              onClick={() => changeMode('simple')}
              className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-left ${
                isSimpleMode
                  ? 'border-teal-500 bg-teal-50/50 shadow-lg shadow-teal-100/50'
                  : 'border-gray-200 bg-white/50 hover:border-teal-300 hover:bg-teal-50/30'
              }`}
            >
              {isSimpleMode && (
                <div className="absolute top-3 right-3">
                  <div className="p-1 bg-teal-500 rounded-full">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-xl ${isSimpleMode ? 'bg-teal-100' : 'bg-gray-100'}`}>
                  <User className={`w-5 h-5 ${isSimpleMode ? 'text-teal-600' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h4 className={`font-semibold ${isSimpleMode ? 'text-teal-700' : 'text-gray-700'}`}>
                    {UI_MODE_LABELS.simple.title}
                  </h4>
                  <span className={`text-xs ${isSimpleMode ? 'text-teal-600' : 'text-gray-500'}`}>
                    {UI_MODE_LABELS.simple.subtitle}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {UI_MODE_LABELS.simple.description}
              </p>
            </button>

            {/* Mode Expert */}
            <button
              onClick={() => changeMode('expert')}
              className={`relative p-5 rounded-2xl border-2 transition-all duration-300 text-left ${
                !isSimpleMode
                  ? 'border-teal-500 bg-teal-50/50 shadow-lg shadow-teal-100/50'
                  : 'border-gray-200 bg-white/50 hover:border-teal-300 hover:bg-teal-50/30'
              }`}
            >
              {!isSimpleMode && (
                <div className="absolute top-3 right-3">
                  <div className="p-1 bg-teal-500 rounded-full">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-xl ${!isSimpleMode ? 'bg-teal-100' : 'bg-gray-100'}`}>
                  <Building2 className={`w-5 h-5 ${!isSimpleMode ? 'text-teal-600' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h4 className={`font-semibold ${!isSimpleMode ? 'text-teal-700' : 'text-gray-700'}`}>
                    {UI_MODE_LABELS.expert.title}
                  </h4>
                  <span className={`text-xs ${!isSimpleMode ? 'text-teal-600' : 'text-gray-500'}`}>
                    {UI_MODE_LABELS.expert.subtitle}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {UI_MODE_LABELS.expert.description}
              </p>
            </button>
          </div>
        </div>

        {/* Couleurs personnalisées */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Palette className="w-5 h-5 text-teal-600" />
            </div>
            {t('settings.appearance.customColors')}
          </h3>

          <div className="bg-teal-50/50 border border-teal-200/50 rounded-2xl p-3 mb-4">
            <p className="text-sm text-teal-800">
              {t('settings.appearance.customColorsDesc')}
            </p>
          </div>

          <div className="bg-blue-50/50 border border-blue-200/50 rounded-2xl p-4 mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={tempColors.useCompanyColors}
                onChange={(e) => setTempColors(prev => ({ ...prev, useCompanyColors: e.target.checked }))}
                className="w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500"
              />
              <div className="ml-3 flex-1">
                <span className="block text-sm font-medium text-gray-900">
                  {t('settings.appearance.useCompanyColors')}
                </span>
                <span className="block text-xs text-gray-600 mt-1">
                  {t('settings.appearance.useCompanyColorsDesc')}
                </span>
              </div>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.appearance.primaryColor')}
              </label>
              <div className="flex items-center gap-3">
                <div
                  className="h-12 w-20 rounded-xl border-2 border-white/50 shadow-lg"
                  style={{ backgroundColor: tempColors.colorPrimary }}
                />
                <input
                  type="text"
                  value={tempColors.colorPrimary}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  className={`${inputClasses} flex-1`}
                  placeholder="#0d9488"
                  maxLength={7}
                />
                <input
                  type="color"
                  value={tempColors.colorPrimary}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  className="h-12 w-12 rounded-xl border-2 border-white/50 cursor-pointer"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.appearance.secondaryColor')}
              </label>
              <div className="flex items-center gap-3">
                <div
                  className="h-12 w-20 rounded-xl border-2 border-white/50 shadow-lg"
                  style={{ backgroundColor: tempColors.colorSecondary }}
                />
                <input
                  type="text"
                  value={tempColors.colorSecondary}
                  onChange={(e) => handleColorChange('secondary', e.target.value)}
                  className={`${inputClasses} flex-1`}
                  placeholder="#14b8a6"
                  maxLength={7}
                />
                <input
                  type="color"
                  value={tempColors.colorSecondary}
                  onChange={(e) => handleColorChange('secondary', e.target.value)}
                  className="h-12 w-12 rounded-xl border-2 border-white/50 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettingsPage;
